import { useMemo } from 'react';
import { performPutRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import type { SaveField } from '@atlassian/jira-issue-field-base/src/common/types.tsx';
import { useEditField } from '@atlassian/jira-issue-field-base/src/services/edit-field-service/main.tsx';
import { useFieldConfig } from '@atlassian/jira-issue-field-base/src/services/field-config-service/main.tsx';
import { SPRINT_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import type { SprintFieldValue, SprintValue } from '../../common/types.tsx';
import type { Props, UseSprintField } from './types.tsx';
import { getActiveOrFutureSprint, getClosedSprints } from './utils.tsx';

export const saveSprintField: SaveField<SprintFieldValue, undefined> = async (
	issueKey,
	fieldKey,
	newValue,
) => {
	const value = getActiveOrFutureSprint(newValue);

	return performPutRequest(`/rest/api/3/issue/${issueKey}/`, {
		body: JSON.stringify({
			fields: {
				// We're only saving the sprint id
				[fieldKey]: value ? value.id : null,
			},
		}),
	});
};

export const useSprintField: UseSprintField = (props: Props) => {
	const { fieldKey, issueId, issueKey, onSuccess, onFailure, initialValue = [] } = props;

	const [{ value: fieldConfig }] = useFieldConfig(issueKey, fieldKey);
	const fieldType = fieldConfig?.schema.custom ?? SPRINT_TYPE;

	const [{ error, value }, { resetError, saveValue }] = useEditField<
		SprintFieldValue,
		null,
		undefined
	>({
		fieldKey,
		fieldType,
		issueId,
		issueKey,
		onSuccess,
		onFailure,
		initialValue,
		saveField: saveSprintField,
	});

	const currentSprint = useMemo<SprintValue | null>(() => getActiveOrFutureSprint(value), [value]);
	const closedSprints = useMemo<SprintFieldValue>(() => getClosedSprints(value), [value]);

	return [
		{ currentSprint, closedSprints, error, value, fieldConfig },
		{ resetError, saveValue },
	];
};
